import Link from 'next/link'
import { Clock, DollarSign, ExternalLink, Package, Ship } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { apiClient } from '@/lib/api/client'
import type { Consignment, SeaTrackingContainer, SeaTrackingShipment } from '@/lib/api/types'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { LoadingState } from '@/src/components/LoadingState'
import { ErrorState } from '@/src/components/ErrorState'
import { getConsignmentTrackingReference, shouldLoadSeaTracking } from '@/src/lib/tracking'

type SeaTrackingDashboardItem = {
  consignment: Consignment
  reference: string
  shipment: SeaTrackingShipment | null
  containers: SeaTrackingContainer[]
  error: string | null
}

async function loadDashboardData() {
  const consignments = await apiClient.listConsignments()
  const seaCandidates = consignments
    .filter(shouldLoadSeaTracking)
    .slice(0, 5)

  const seaTracking = await Promise.all(
    seaCandidates.map(async (consignment): Promise<SeaTrackingDashboardItem> => {
      const reference = getConsignmentTrackingReference(consignment) || `Consignment ${consignment.id.slice(0, 8)}`

      try {
        const [shipment, containerPage] = await Promise.all([
          apiClient.getSeaTrackingShipment(reference),
          apiClient.listSeaTrackingContainers(reference, 1, 10),
        ])

        return {
          consignment,
          reference,
          shipment,
          containers: containerPage.containers,
          error: null,
        }
      } catch (error) {
        return {
          consignment,
          reference,
          shipment: null,
          containers: [],
          error: error instanceof Error ? error.message : 'Visiwise lookup failed',
        }
      }
    }),
  )

  return { consignments, seaTracking }
}

export function DashboardPage() {
  const { data, error, loading, reload } = useAsyncData(loadDashboardData, [])

  if (loading) return <LoadingState label="Loading dashboard..." />
  if (error) return <ErrorState error={error} onRetry={reload} />

  const safeConsignments = data?.consignments ?? []
  const seaTrackingItems = data?.seaTracking ?? []
  const totalTaxEstimated = safeConsignments.reduce(
    (sum, consignment) => sum + (consignment.total_tax_estimate ?? 0),
    0,
  )
  const stats = {
    totalConsignments: safeConsignments.length,
    pendingEstimates: safeConsignments.filter((consignment) => consignment.status === 'pending').length,
    seaTracked: safeConsignments.filter(shouldLoadSeaTracking).length,
    totalTaxEstimated,
  }
  const recentConsignments = safeConsignments.slice(0, 5)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="mt-1 text-muted-foreground">Welcome back. Here is your import overview.</p>
        </div>
        <Link href="/consignments/new">
          <Button>+ New Consignment</Button>
        </Link>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Consignments</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalConsignments}</div>
            <p className="mt-1 text-xs text-muted-foreground">All active records</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Estimates</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingEstimates}</div>
            <p className="mt-1 text-xs text-muted-foreground">Awaiting processing</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sea Tracking</CardTitle>
            <Ship className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.seaTracked}</div>
            <p className="mt-1 text-xs text-muted-foreground">Container-linked records</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tax Estimated</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              UGX {stats.totalTaxEstimated.toLocaleString()}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Visiwise Sea Tracking</CardTitle>
          <CardDescription>Recent sea/container consignments with live tracking lookup.</CardDescription>
        </CardHeader>
        <CardContent>
          {seaTrackingItems.length > 0 ? (
            <div className="space-y-3">
              {seaTrackingItems.map((item) => {
                const status = item.shipment?.process_status || item.shipment?.data_status || 'Pending'
                const movement = describeContainerMovement(item.containers[0])
                const dashboardUrl = item.shipment?.dashboard_url

                return (
                  <div key={item.consignment.id} className="grid gap-3 rounded-md border p-3 md:grid-cols-[1fr_auto]">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link href={`/consignments/${item.consignment.id}/tracking`} className="font-medium hover:underline">
                          {item.reference}
                        </Link>
                        <Badge variant={item.error ? 'destructive' : 'secondary'}>{item.error ? 'Lookup Failed' : formatLabel(status)}</Badge>
                      </div>
                      <p className="mt-1 truncate text-sm text-muted-foreground">
                        {item.consignment.customer?.company_name || item.consignment.customer?.name || item.consignment.destination_port}
                      </p>
                      <p className="mt-1 text-sm">{item.error || movement}</p>
                    </div>
                    <div className="flex items-center gap-3 md:justify-end">
                      <div className="text-right text-sm">
                        <p className="font-medium">{item.containers.length.toLocaleString()} containers</p>
                        <p className="text-muted-foreground">{formatDateTime(item.shipment?.last_successful_tracking_at)}</p>
                      </div>
                      {dashboardUrl && (
                        <a
                          href={dashboardUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md border hover:bg-muted"
                          aria-label={`Open ${item.reference} in Visiwise`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No sea/container consignments with tracking references yet.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Consignments</CardTitle>
          <CardDescription>Your latest import activities</CardDescription>
        </CardHeader>
        <CardContent>
          {recentConsignments.length > 0 ? (
            <div className="space-y-3">
              {recentConsignments.map((consignment) => (
                <Link
                  key={consignment.id}
                  href={`/consignments/${consignment.id}`}
                  className="flex items-center justify-between rounded-md border p-3 transition-colors hover:bg-muted/50"
                >
                  <div>
                    <p className="font-medium">{getConsignmentTrackingReference(consignment) || `Consignment #${consignment.id.slice(0, 8)}`}</p>
                    <p className="text-sm text-muted-foreground">{consignment.destination_port}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium capitalize">{consignment.status || 'pending'}</p>
                    <p className="text-sm text-muted-foreground">
                      UGX {(consignment.total_tax_estimate ?? 0).toLocaleString()}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No consignments yet. Create your first one to get started.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return 'Not synced'
  return new Date(value).toLocaleString()
}

function describeContainerMovement(container: SeaTrackingContainer | undefined) {
  if (!container) return 'No container movement has been returned by Visiwise yet.'
  const movement = container.last_movement
  const event = readString(movement, 'event') || readString(movement, 'status') || readString(movement, 'description')
  const location = readString(movement, 'location') || container.pod_terminal_name || readString(container.pod, 'name')
  const vessel = [container.arriving_vessel_name, container.arriving_vessel_voyage_no].filter(Boolean).join(' ')

  return [
    event ? formatLabel(event) : null,
    location ? `at ${location}` : null,
    vessel ? `via ${vessel}` : null,
  ].filter(Boolean).join(' ') || 'Container movement is available in Visiwise.'
}

function readString(value: Record<string, unknown> | null | undefined, key: string) {
  const entry = value?.[key]
  return typeof entry === 'string' && entry.trim() ? entry.trim() : null
}
